import { useState, useEffect, useMemo, useCallback } from 'react';
import { useReviewDetailGet, invalidateReviewDetail } from '../api/get-review-detail';
import { useReviewEventPatch } from '../api/patch-review-event';
import { useSubmitReview } from '../api/post-submit-review';
import { REVIEW_STATUS, DETECTION_TYPES_CONFIG } from 'constants/av-review';
import { VERDICT_STATES } from '../constants';
import {
  groupEventsByType,
  isViewingThresholdMet as checkThreshold,
  getNextUnreviewedDetection,
  getNextVideoUrl,
  isInvalidReasonRequired,
} from '../helpers';

const useReviewSession = reviewId => {
  const { data, get, isProcessing, isStale } = useReviewDetailGet(reviewId);

  const [activeDetectionType, setActiveDetectionType] = useState(null);
  const [activeEventIndex, setActiveEventIndex] = useState(0);
  const [activeChunkIndex, setActiveChunkIndex] = useState(0);
  const [reReviewIndex, setReReviewIndex] = useState(null);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [dataAtPatch, setDataAtPatch] = useState(null);

  // True until the GET refetch replaces `data`. Assumes single consumer of this review id.
  const isVerdictRefreshing = dataAtPatch !== null && dataAtPatch === data;

  useEffect(() => {
    if (reviewId) get(reviewId);
  }, [get, reviewId]);

  useEffect(() => {
    if (isStale && !isProcessing) get(reviewId);
  }, [isStale, isProcessing, get, reviewId]);

  const detections = useMemo(() => (data?.events ? groupEventsByType(data.events) : {}), [
    data?.events,
  ]);

  useEffect(() => {
    if (!activeDetectionType && data?.events) {
      const types = Object.keys(detections);
      if (types.length > 0) setActiveDetectionType(types[0]);
    }
  }, [data?.events, activeDetectionType, detections]);

  const reviewProgress = useMemo(() => {
    const types = Object.values(detections);
    const total = types.length;
    const reviewed = types.filter(
      d => d.verdict !== VERDICT_STATES.PENDING && d.verdict !== VERDICT_STATES.REVIEWING,
    ).length;
    return { reviewed, total };
  }, [detections]);

  const activeDetection = useMemo(() => detections[activeDetectionType] || null, [
    detections,
    activeDetectionType,
  ]);
  const activeEvent = useMemo(() => activeDetection?.events?.[activeEventIndex] || null, [
    activeDetection,
    activeEventIndex,
  ]);
  const activeChunk = useMemo(() => activeEvent?.event_data?.[activeChunkIndex] || null, [
    activeEvent,
    activeChunkIndex,
  ]);

  const overlayText = useMemo(() => {
    if (!activeEvent || !activeDetectionType) return '';
    const detectionLabel =
      DETECTION_TYPES_CONFIG[activeDetectionType]?.label || activeDetectionType;
    return `Event${activeEventIndex + 1} \u00B7 Chunk${activeChunkIndex +
      1} \u00B7 ${detectionLabel}`;
  }, [activeEvent, activeDetectionType, activeEventIndex, activeChunkIndex]);

  const nextVideoUrl = useMemo(
    () => getNextVideoUrl(activeDetection?.events, activeEventIndex, activeChunkIndex),
    [activeDetection, activeEventIndex, activeChunkIndex],
  );
  const viewingThresholdMet = useMemo(() => checkThreshold(activeEvent), [activeEvent]);
  const isAllEventsReviewed = useMemo(() => {
    if (!activeDetection) return false;
    const reasonRequired = isInvalidReasonRequired(activeDetectionType);
    return activeDetection.events.every(e => {
      if (e.is_valid == null) return false;
      if (e.is_valid === false && reasonRequired && !e.invalid_reason) return false;
      return true;
    });
  }, [activeDetection, activeDetectionType]);
  const nextUnreviewedType = useMemo(
    () => getNextUnreviewedDetection(detections, activeDetectionType),
    [detections, activeDetectionType],
  );
  const allDetectionsVerdicted = useMemo(() => {
    const entries = Object.entries(detections);
    if (entries.length === 0) return false;
    return entries.every(([type, d]) => {
      if (
        d.verdict === VERDICT_STATES.PENDING ||
        d.verdict === VERDICT_STATES.REVIEWING
      ) {
        return false;
      }
      if (!isInvalidReasonRequired(type)) return true;
      return d.events.every(e => e.is_valid !== false || !!e.invalid_reason);
    });
  }, [detections]);

  const submitBarSummary = useMemo(() => {
    if (!allDetectionsVerdicted) return '';
    const types = Object.values(detections);
    const validCount = types.filter(d => d.verdict === VERDICT_STATES.VALID).length;
    const invalidCount = types.filter(d => d.verdict === VERDICT_STATES.INVALID).length;
    return `All detections reviewed \u2014 ${validCount} valid, ${invalidCount} invalid`;
  }, [allDetectionsVerdicted, detections]);

  const handlePatchComplete = useCallback(
    error => {
      if (error) {
        setDataAtPatch(null);
        return;
      }
      invalidateReviewDetail(reviewId);
    },
    [reviewId],
  );

  const { patch, isProcessing: isPatchProcessing } = useReviewEventPatch({
    onComplete: handlePatchComplete,
  });

  const isVerdictBusy = isPatchProcessing || isVerdictRefreshing;

  const handleSubmitComplete = useCallback(
    error => {
      if (error) {
        setIsSubmittingReview(false);
        return;
      }
      invalidateReviewDetail(reviewId);
    },
    [reviewId],
  );

  const { post: submitReviewPost } = useSubmitReview({
    onComplete: handleSubmitComplete,
  });

  const isReviewSubmitted = data?.status === REVIEW_STATUS.SUBMITTED;

  useEffect(() => {
    if (isReviewSubmitted) setIsSubmittingReview(false);
  }, [isReviewSubmitted]);

  const selectDetection = useCallback(type => {
    setActiveDetectionType(type);
    setActiveEventIndex(0);
    setActiveChunkIndex(0);
    setReReviewIndex(null);
  }, []);

  const selectEvent = useCallback(index => {
    setActiveEventIndex(index);
    setActiveChunkIndex(0);
  }, []);

  const goToNextChunk = useCallback(() => {
    if (!activeEvent) return;
    const maxIndex = activeEvent.event_data.length - 1;
    setActiveChunkIndex(prev => Math.min(prev + 1, maxIndex));
  }, [activeEvent]);

  const goToPrevChunk = useCallback(() => {
    setActiveChunkIndex(prev => Math.max(prev - 1, 0));
  }, []);

  const markChunkViewed = useCallback(() => {
    if (!activeEvent || !activeChunk || activeChunk.is_viewed || isVerdictBusy) return;
    patch(activeEvent.id, {
      event_data: [{ chunk_index: activeChunk.chunk_index, is_viewed: true }],
    });
  }, [activeEvent, activeChunk, isVerdictBusy, patch]);

  const submitVerdict = useCallback(
    (isValid, invalidReason = null) => {
      if (!activeEvent || isVerdictBusy || isReviewSubmitted) return;
      const sameVerdict = activeEvent.is_valid === isValid;
      const sameReason = (activeEvent.invalid_reason || null) === (invalidReason || null);
      if (sameVerdict && (isValid === true || sameReason)) {
        setReReviewIndex(null);
        return;
      }
      const payload = { is_valid: isValid };
      if (isValid === false && invalidReason !== null)
        payload.invalid_reason = invalidReason;
      setDataAtPatch(data);
      patch(activeEvent.id, payload);
      if (!sameVerdict) setReReviewIndex(null);
    },
    [activeEvent, isVerdictBusy, isReviewSubmitted, patch, data],
  );

  useEffect(() => {
    if (dataAtPatch !== null && dataAtPatch !== data) setDataAtPatch(null);
  }, [dataAtPatch, data]);

  const submitReview = useCallback(() => {
    if (!allDetectionsVerdicted || isSubmittingReview || isReviewSubmitted) return;
    setIsSubmittingReview(true);
    submitReviewPost(reviewId);
  }, [
    allDetectionsVerdicted,
    isSubmittingReview,
    isReviewSubmitted,
    submitReviewPost,
    reviewId,
  ]);

  const reReviewEvent = useCallback(index => {
    setReReviewIndex(index);
    setActiveEventIndex(index);
    setActiveChunkIndex(0);
  }, []);

  const cancelReReview = useCallback(() => {
    setReReviewIndex(null);
  }, []);

  const goToNextDetection = useCallback(() => {
    if (nextUnreviewedType) selectDetection(nextUnreviewedType);
  }, [nextUnreviewedType, selectDetection]);

  return {
    data,
    isLoading: !data && isProcessing,
    isVerdictBusy,
    detections,
    reviewProgress,
    activeDetectionType,
    activeDetection,
    activeEventIndex,
    activeEvent,
    activeChunkIndex,
    activeChunk,
    nextVideoUrl,
    overlayText,
    viewingThresholdMet,
    isShowingSummary:
      isAllEventsReviewed && reReviewIndex === null && !isVerdictRefreshing,
    isReReview: reReviewIndex !== null,
    nextUnreviewedType,
    submitBarSummary,
    allDetectionsVerdicted,
    isReviewSubmitted,
    isSubmitProcessing: isSubmittingReview,
    selectDetection,
    selectEvent,
    goToNextChunk,
    goToPrevChunk,
    selectChunk: setActiveChunkIndex,
    markChunkViewed,
    submitVerdict,
    reReviewEvent,
    cancelReReview,
    goToNextDetection,
    submitReview,
  };
};

export default useReviewSession;
